import {includes, replace} from 'lodash'
import Network from '@/services/network'
import {ref} from 'vue'
import useDate from '@/services/utils/day'
import figureFormatter from '@/services/utils/figureFormatter.js'

const {
    firstDateOfQuarter,
    lastDateOfQuarter,
    lastDateOfYear,
    firstDateOfYear,
    firstDateOfLastYear,
    lastDateOfLastYear,
    firstDateOfMonth,
    lastDateOfMonth,
    firstDateOfLastMonth,
    lastDateOfLastMonth
} = useDate()

const network = new Network

// Cache key for localStorage
const DASHBOARD_CACHE_KEY = 'dashboard_summaries_cache'

// Get cache key based on company and module
const getCacheKey = (companyId, moduleId) => {
    return `${companyId}_${moduleId}`
}

// Get cached data from localStorage
const getCache = (companyId, moduleId) => {
    try {
        const cacheStr = localStorage.getItem(DASHBOARD_CACHE_KEY)
        if (!cacheStr) return null
        
        const cache = JSON.parse(cacheStr)
        const key = getCacheKey(companyId, moduleId)
        
        return cache[key] || null
    } catch (e) {
        localStorage.removeItem(DASHBOARD_CACHE_KEY)
        return null
    }
}

// Save data to localStorage cache
const setCache = (companyId, moduleId, data) => {
    try {
        const cacheStr = localStorage.getItem(DASHBOARD_CACHE_KEY)
        let cache = {}
        cache = cacheStr ? JSON.parse(cacheStr) : {}
        const key = getCacheKey(companyId, moduleId)
        
        cache[key] = data
        localStorage.setItem(DASHBOARD_CACHE_KEY, JSON.stringify(cache))
    } catch (e) {
        console.error('Failed to cache dashboard summaries:', e)
    }
}

export function useCardStatistics(params) {
    const statisticsItem = ref({});
    const {commaFormat} = figureFormatter()

    const makeStatistics = (statisticsData, notIncludeArr) => {
        let statistics = []
        Object.keys(statisticsData).forEach((key) => {
            if (!includes(notIncludeArr, key)) {
                statistics.push(
                    {
                        name: replace(key, /[\_]/g, ' '),
                        value: commaFormat(statisticsData[key]),
                        key: key
                    }
                )
            }
        })
        return statistics
    }

    function getDashboardData(query = '') {
        network.api('get', `/get/dashboard?company_id=${params.companyId}&module_id=${params.moduleId}` + query)
            .then(res => {
                if (res.status) {
                    statisticsItem.value = res.data
                }
            })
    }

    async function getDashboardSummariesFromCache(query = '') {
        const cache = getCache(params.companyId, params.moduleId)
        if (cache) {
            statisticsItem.value = cache
            return cache
        }
        return await getDashboardSummaries(query)
    }

    function getDashboardSummaries(query = '', ignoreCacheUpdate = false) {
        return network.api('get', `/get/dashboard/summaries?company_id=${params.companyId}&module_id=${params.moduleId}` + query)
            .then(res => {
                if (res.status) {
                  statisticsItem.value = res.data
                    if (! ignoreCacheUpdate) {
                      setCache(params.companyId, params.moduleId, res.data)
                    }
                  return res.data
                }
                return null
            })
    }

    function getDashboardSummaryByKey(query = '', dashboardKey) {
        if(! dashboardKey) {
          return;
        }
        
        network.api('get', `/get/dashboard/summary-by-key?company_id=${params.companyId}&module_id=${params.moduleId}` + query)
            .then(res => {
                if (res.status) {
                  statisticsItem.value = {
                    ...statisticsItem.value,
                    [dashboardKey]: res.data
                  }
                }
            })
    }

    //`"start":"","end":""`
    const timeRange = [
      {
        name: 'This Month',
        value: 'this_month'
      },
      {
        name: 'Last Month',
        value: 'last_month'
      },
      {
        name: 'This Quarter',
        value: 'this_quarter'
      },
      {
        name: 'This Year',
        value: 'this_year'
      },
      {
        name: 'Last Year',
        value: 'last_year'
      }
    ];

    return {
        makeStatistics,
        timeRange,
        getDashboardData,
        getDashboardSummaries,
        getDashboardSummaryByKey,
        getDashboardSummariesFromCache,
        statisticsItem,
    }
}
